import type {
  CallSortablePanelItem,
  CallMinActionsProps,
  CallOperateProps,
} from '@halo/ui-chat-react';
import {
  CallMinWindowPanel,
  MediaCallDrag,
  MediaCallDragLabel,
  CallMaxWindowPanel,
  CallMaxActions,
  CallerInfo,
  CallStatusDesc,
  PinSidePanel,
  CallMainPanelNotification,
  CallMainPanelFooter,
  CallCardPanel,
  CallMinActions,
  CallMainPanel,
  CallSortablePanel,
  CallNewJoin,
  MediaActionStatus,
  MediaActionType,
  UserRoomInfo,
  toaster,
  CallNetWorkStatus,
} from '@halo/ui-chat-react';
import { useDragMove, useColorAnalyzer, useAudio } from '@halo/hooks';
import type { CallMemberItem } from '@halo/chat-sdk-react';
import { SubscribeStatus } from '@halo/chat-sdk-react';
import { MediaSDKDeviceType } from '@halo/chat-sdk-react';
import {
  CallStatus,
  isSessionCategory,
  MediaDeviceStatus,
  MediaScreenSharingStatus,
  MediaSDKMediaType,
  MediaStatus,
  MsgPayloadType,
  SessionCategoryPrefix,
  useAppStore,
} from '@halo/chat-sdk-react';
import { useStoreEnhance } from '@halo/zustand-enhance';
import { VStack } from '@chakra-ui/react';
import { useMediaError } from '@/scripts/hooks';
import { CallInviteDialog } from './CallInviteDialog';
import { CallMembers } from './CallMembers';

interface UseMediaCallDialogProps {
  session_id: string | undefined;
}

function useMediaCallDialog({ session_id }: UseMediaCallDialogProps) {
  const { play: playCallSound, stop: stopCallSound } = useAudio({
    src: '/media/call.mp3',
    loop: true,
  });
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [gridCols, setGridCols] = useState<number>(0);
  const [gridContainerInfo, setGridContainerInfo] = useState({ width: 0, height: 0 });
  const [callMembers, setCallMembers] = useState<any[]>([]);

  const appStore = useAppStore();
  const {
    client,
    mediaActivatedId,
    mediaStores,
    sessionStores,
    user: currentUser,
    userRoomInfo,
    setUserRoomInfo,
  } = appStore;

  const sessionStoreInstance = useMemo(() => {
    return sessionStores[session_id as string];
  }, [session_id, sessionStores]);

  const mediaStoreInstance = useMemo(() => {
    return mediaStores[session_id as string];
  }, [session_id, mediaStores]);

  const activeMediaStoreInstance = useMemo(() => {
    return mediaStores[mediaActivatedId as string];
  }, [mediaActivatedId, mediaStores]);

  const mediaStore = useStoreEnhance(mediaStoreInstance);
  const activeMediaStore = useStoreEnhance(activeMediaStoreInstance);
  const sessionStore = useStoreEnhance(sessionStoreInstance);
  const { fullScreen, setFullScreen } = useMemo(
    () => ({
      fullScreen: !!mediaStore?.fullScreen,
      setFullScreen: mediaStore?.setFullScreen ?? (() => {}),
    }),
    [mediaStore?.fullScreen, mediaStore?.setFullScreen]
  );

  const isSingle = useMemo(() => {
    return isSessionCategory(mediaStore?.session?.chat_id, SessionCategoryPrefix.Single);
  }, [mediaStore?.session?.chat_id]);

  const isCaller = useMemo(() => {
    return mediaStore?.caller?.user_id === currentUser?.user_id;
  }, [mediaStore?.caller?.user_id, currentUser?.user_id]);

  const user = useMemo(() => {
    return mediaStore?.mediaUser;
  }, [mediaStore?.mediaUser]);

  const members = useMemo(() => {
    const members = mediaStore?.membersId
      ?.map((id) => {
        const member = mediaStore?.membersMap[id];
        if (member) {
          return member;
        }
      })
      .filter((item) => !!item);
    return members || [];
  }, [mediaStore?.membersId, mediaStore?.membersMap]);

  const inviteUsers = useMemo(() => {
    return (
      mediaStore?.inviteUsers?.filter((user) => user?.user_id !== currentUser?.user_id) || []
    );
  }, [currentUser?.user_id, mediaStore?.inviteUsers]);

  const miniProps = useMemo(() => {
    if (isSingle) {
      const id = mediaStore?.membersId?.find((id) => !id.includes(currentUser?.user_id));
      return mediaStore?.membersMap[id!] || mediaStore?.session?.user_info;
    }
    return mediaStore?.session;
  }, [
    mediaStore?.membersId,
    mediaStore?.membersMap,
    mediaStore?.session,
    isSingle,
    currentUser?.user_id,
  ]);

  const fullProps = useMemo(() => {
    const members: CallSortablePanelItem[] = [];
    const fixedMembers: CallSortablePanelItem[] = [];

    callMembers.forEach((member) => {
      if (member.fixed) {
        fixedMembers.push(member);
      } else if (member.id !== user?.user_id) {
        members.push(member);
      }
    });

    return {
      members,
      fixedMembers,
    };
  }, [callMembers, user?.user_id]);

  const pinSidePanelMember = useMemo(
    () => (fullProps.fixedMembers.length ? fullProps.fixedMembers[0] : fullProps.members[0]),
    [fullProps.fixedMembers, fullProps.members]
  );

  const isOnly = useMemo(() => {
    return callMembers.length === 1;
  }, [callMembers]);

  const devicesProps = useMemo(() => {
    return {
      devices: Object.entries(mediaStore?.devices || {}).reduce((acc, [key, value]) => {
        acc[key] = {
          ...value,
          list: value.list.map((item) => ({
            key: item.deviceId,
            label: item.label,
          })),
        };
        return acc;
      }, {} as any),
    };
  }, [mediaStore?.devices]);

  const { dragRef, hoverOverRef, hoverLeaveRef, direction, isMouseOver, style } = useDragMove({
    disabled: fullScreen,
    marginTop: 100,
    thresholdTop: 200,
  });

  const bgColor = useColorAnalyzer(user?.avatar, {
    format: 'rgb',
    samplingStep: 8,
    defaultColor: '#3d4043',
    // alpha: 0.5,
  });

  const isConnect = useMemo(() => {
    return mediaStore?.status === MediaStatus.JOIN;
  }, [mediaStore?.status]);

  const callNetWorkStatus = useMemo(() => {
    if (mediaStore?.callStatus === CallStatus.NETWORK_RECONNECTING) {
      return CallNetWorkStatus.RECONNECTING;
    } else if (mediaStore?.callStatus === CallStatus.NETWORK_ERROR) {
      return CallNetWorkStatus.ERROR;
    }
  }, [mediaStore?.callStatus]);

  const handleFixed = useCallback((item: any) => {
    setCallMembers((prevMembers) =>
      prevMembers.map((member) => ({
        ...member,
        fixed: item.id === member.id ? !member.fixed : false,
      }))
    );
  }, []);

  const handleSubscribe = useCallback((item: CallMemberItem, status: SubscribeStatus) => {
    setCallMembers((prevMembers) =>
      prevMembers.map((member) => ({
        ...member,
        subscribeStatus: member.id === item.id ? status : member.subscribeStatus,
      }))
    );
  }, []);

  const [audioInfo, setAudioInfo] = useState({ hasAudioStream: false, hasVolume: false });

  const renderCallCard = () => {
    if (fullProps.fixedMembers.length === 0) return;
    return fullProps.fixedMembers?.map((item) => (
      <CallCardPanel
        key={item.id}
        member={item}
        rearChildren={
          <CallMainPanelFooter
            disabled={false}
            member={item}
            handleDrawSpectrum={mediaStore?.drawSpectrum}
            onAudioInfo={(info) => setAudioInfo(info)}
          />
        }
        onFixed={(item) => handleFixed(item)}
        ondbClick={(member) => setUserRoomInfo({ open: true, member })}
        avatar={item?.avatar}
        avatarSize={2000}
      >
        <CallerInfo
          avatar={item?.avatar}
          name={item?.name}
          isConnect={false}
          size="3xl"
          showBg={audioInfo.hasAudioStream}
          hasVolume={audioInfo.hasVolume}
        />
        <CallStatusDesc
          isSingle={isSingle}
          isCaller={isCaller}
          callStatus={mediaStore?.callStatus}
          mediaType={mediaType}
          fullScreen={fullScreen}
        />
      </CallCardPanel>
    ));
  };

  useEffect(() => {
    if (!gridContainerRef.current) return;
    const observe = new ResizeObserver(([dom]) => {
      setGridContainerInfo({
        width: (dom.target as HTMLDivElement)?.offsetWidth,
        height: (dom.target as HTMLDivElement)?.offsetHeight,
      });
    });
    observe.observe(gridContainerRef.current);
    return () => observe.disconnect();
  }, [callMembers, fullScreen]);

  const gridTemplateColumns = useMemo(
    () => `repeat(${fullScreen ? gridCols : 1}, 1fr)`,
    [fullScreen, gridCols]
  );

  const gridRows = useMemo(() => {
    if (fullProps.fixedMembers.length) {
      if (fullProps.members.length > 4) {
        return 3;
      } else if (fullProps.members.length >= 2) {
        return 2;
      }
    } else {
      if (fullProps.members.length > 6) {
        return 3;
      } else if (fullProps.members.length > 2) {
        return 2;
      }
    }
    return 1;
  }, [fullProps.members.length, fullProps.fixedMembers.length]);

  const gridAutoRowsHeight = useMemo(
    () =>
      (gridContainerInfo.height - (fullScreen && gridRows > 1 ? (gridRows - 1) * 10 : 0)) /
      gridRows,
    [fullScreen, gridContainerInfo.height, gridRows]
  );

  const mediaType = useMemo(() => {
    return mediaStore?.type === MsgPayloadType.MEDIA_AUDIO_CALL
      ? MediaActionType.AUDIO
      : MediaActionType.VIDEO;
  }, [mediaStore?.type]);

  const handleDragEnd = useCallback((newItems: Record<string, any>[]) => {
    setCallMembers((prev) => [
      ...newItems,
      ...prev.filter((item) => !newItems.find((newItem) => newItem.id === item.id)),
    ]);
  }, []);

  const getGridCols = useCallback((counts: number) => {
    if (counts < 2) return 1;
    if (counts < 5) return 2;
    return 3;
  }, []);

  const getFixedGridCols = useCallback((counts: number) => (counts <= 2 ? 1 : 2), []);

  useEffect(() => {
    setGridCols(
      fullProps.fixedMembers.length
        ? getFixedGridCols(fullProps.members.length)
        : getGridCols(fullProps.members.length)
    );
  }, [fullProps.members.length, fullProps.fixedMembers.length, getFixedGridCols, getGridCols]);

  useEffect(() => {
    const data =
      mediaStore?.membersId?.flatMap((id) => {
        const member = mediaStore?.membersMap[id];

        const isScreenSharing = !!member?.callActionMap?.screenSharing_video;

        if (!member) {
          return [];
        }

        const baseMember = {
          ...member,
          screenSharing: false,
          fixed: false,
          subscribeStatus: SubscribeStatus.DEFAULT,
        };

        if (!isScreenSharing) return [baseMember];

        const screenMember = {
          ...baseMember,
          screenSharing: true,
          id: `${member.id}_ScreenSharing`,
        };

        return [screenMember, baseMember];
      }) || [];

    setCallMembers((prevCallMembers) => {
      const resData = data.sort((a, b) => {
        const indexA = prevCallMembers.findIndex((member) => member.id === a.id);
        const indexB = prevCallMembers.findIndex((member) => member.id === b.id);
        return indexA - indexB;
      });

      return resData
        .map((item, index) => {
          const curMemberIndex = prevCallMembers.findIndex((member) => member.id === item.id);
          if (curMemberIndex > -1) {
            const prevMember = prevCallMembers[curMemberIndex];
            return {
              ...prevMember,
              ...item,
              fixed: prevMember.fixed,
              subscribeStatus: prevMember.subscribeStatus,
              sortIndex: index,
            };
          } else {
            return {
              ...item,
              sortIndex: item.screenSharing
                ? -1
                : resData.findIndex((member) => member.id !== item.id && !member.screenSharing),
            };
          }
        })
        .sort((a, b) => a.sortIndex - b.sortIndex);
    });
  }, [mediaStore?.membersId, mediaStore?.membersMap]);

  useEffect(() => {
    if (mediaStore?.callStatus && CallStatus.RINGING === mediaStore?.callStatus) {
      playCallSound();
      mediaStore?.startCalling();
    } else {
      stopCallSound();
      mediaStore?.stopCalling();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaStore?.callStatus, playCallSound, stopCallSound]);

  const startTime = useMemo(() => {
    if (client && mediaStore?.status === MediaStatus.JOIN) {
      return client.serverTime.time;
    }
  }, [mediaStore?.status, client]);

  return {
    appStore,
    mediaStore,
    sessionStore,
    session_id,
    mediaActivatedId,
    activeMediaStore,
    devicesProps,
    fullScreen,
    setFullScreen,
    dragRef,
    style,
    hoverOverRef,
    hoverLeaveRef,
    direction,
    isMouseOver,
    callNetWorkStatus,
    bgColor,
    user,
    miniProps,
    fullProps,
    gridContainerRef,
    gridTemplateColumns,
    gridAutoRowsHeight,
    handleDragEnd,
    renderCallCard,
    isConnect,
    mediaType,
    isSingle,
    callMembers,
    members,
    inviteUsers,
    isOnly,
    handleFixed,
    handleSubscribe,
    pinSidePanelMember,
    startTime,
    isCaller,
    userRoomInfo,
    setUserRoomInfo,
  };
}

type MediaBaseDialogProps = ReturnType<typeof useMediaCallDialog> & {
  actionsProps?: Partial<CallMinActionsProps>;
  maxActionsProps?: Partial<CallOperateProps>;
};

function MediaBaseDialog({
  mediaStore,
  devicesProps,
  fullScreen,
  setFullScreen,
  dragRef,
  style,
  hoverOverRef,
  hoverLeaveRef,
  direction,
  isMouseOver,
  callNetWorkStatus,
  bgColor,
  user,
  miniProps,
  fullProps,
  gridContainerRef,
  gridTemplateColumns,
  gridAutoRowsHeight,
  handleDragEnd,
  renderCallCard,
  isConnect,
  mediaType,
  actionsProps,
  maxActionsProps,
  isSingle,
  members,
  callMembers,
  inviteUsers,
  isOnly,
  handleFixed,
  handleSubscribe,
  pinSidePanelMember,
  startTime,
  isCaller,
  userRoomInfo,
  setUserRoomInfo,
}: MediaBaseDialogProps) {
  const { mediaError } = useMediaError();
  const [openAddCallMembers, setOpenAddCallMembers] = useState<boolean>(false);
  const [openInviteCallMembers, setOpenInviteCallMembers] = useState<boolean>(false);

  useEffect(() => {
    if (openInviteCallMembers && mediaStore?.membersId?.length) {
      mediaStore?.removeMembersFromAllTimeouts(mediaStore?.membersId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openInviteCallMembers]);

  if (!mediaStore) {
    return null;
  }

  return (
    <>
      <MediaCallDrag
        fullScreen={fullScreen}
        dragRef_={{
          dragRef,
          style,
        }}
      >
        {fullScreen ? (
          <CallMaxWindowPanel
            sideChildren={
              isConnect &&
              !isOnly && (
                <PinSidePanel
                  avatar={user?.avatar}
                  disabled={false}
                  bg={bgColor}
                  member={user}
                  ondbClick={(member) => setUserRoomInfo({ open: true, member })}
                  handleDrawSpectrum={mediaStore.drawSpectrum}
                />
              )
            }
            frontChildren={
              callNetWorkStatus && <CallMainPanelNotification status={callNetWorkStatus} />
            }
          >
            {isConnect ? (
              <CallMainPanel
                rightRef={gridContainerRef}
                fixedMembers={fullProps.fixedMembers}
                pinChildren={renderCallCard()}
                rightChildren={
                  isOnly ? (
                    <PinSidePanel
                      avatar={user?.avatar}
                      avatarSize={2000}
                      isOnly={true}
                      disabled={true}
                      bg={bgColor}
                      member={user}
                      ondbClick={(member) => setUserRoomInfo({ open: true, member })}
                      handleDrawSpectrum={mediaStore.drawSpectrum}
                    />
                  ) : (
                    <CallSortablePanel
                      noFixedMembers={fullProps.members}
                      gridTemplateColumns={gridTemplateColumns}
                      gridAutoRowsHeight={gridAutoRowsHeight}
                      fullScreen={fullScreen}
                      ondbClick={(member) => setUserRoomInfo({ open: true, member })}
                      handleDragEnd={handleDragEnd}
                      handleDrawSpectrum={mediaStore.drawSpectrum}
                      onFixed={(member) => handleFixed(member)}
                      subscribeCollection={mediaStore.subscribeCollection}
                      onSubscribe={handleSubscribe}
                    ></CallSortablePanel>
                  )
                }
              />
            ) : (
              <VStack
                flex="1"
                justify={'center'}
                w="full"
                position="relative"
                borderRadius="md"
                bg={
                  miniProps?.avatar
                    ? 'lightgray'
                    : `linear-gradient( rgba(0,0,0,0.7))${bgColor ? `, ${bgColor}` : ''}`
                }
                _before={
                  miniProps?.avatar
                    ? {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        bgImage: `url(${miniProps?.avatar})`,
                        bgSize: `${2000}px ${2000}px`,
                        bgPos: 'center',
                        bgRepeat: 'no-repeat',
                        filter: `blur(${2000 / 10}px)`,
                        zIndex: -1,
                      }
                    : undefined
                }
                overflow="hidden"
              >
                <CallerInfo
                  avatar={miniProps?.avatar}
                  name={miniProps?.name}
                  isConnect={false}
                  size="3xl"
                  showBg
                  hasVolume
                  playbackRate={1}
                />
                <CallStatusDesc
                  isSingle={isSingle}
                  callMembers={mediaStore.inviteUsers}
                  caller={mediaStore?.caller}
                  isCaller={isCaller}
                  callStatus={mediaStore?.callStatus}
                  mediaType={mediaType}
                  fullScreen={fullScreen}
                />
              </VStack>
            )}

            <CallMaxActions
              name={Object.entries(members || {})
                ?.map(([_, item]) => item?.name)
                .join('、')}
              videoState={mediaStore?.devices?.videoinput?.status === MediaDeviceStatus.ENABLED}
              speakerState={mediaStore?.devices?.audioinput?.status === MediaDeviceStatus.ENABLED}
              muteState={mediaStore?.devices?.audiooutput?.status === MediaDeviceStatus.ENABLED}
              screenState={
                mediaStore?.screenSharing?.[MediaSDKMediaType.SCREENSHARING_VIDEO]?.status ===
                MediaScreenSharingStatus.ENABLED
              }
              callStatus={mediaStore?.callStatus}
              mediaIsReady={mediaStore?.status === MediaStatus.JOIN}
              startTime={startTime}
              setZoom={setFullScreen}
              onHangup={mediaStore?.hangup}
              onDeviceChange={(deviceType, deviceId) => {
                mediaStore
                  ?.selectDevice(deviceType as MediaSDKDeviceType, deviceId)
                  .catch((err) => {
                    toaster.warning({ title: err.message || 'Device use error' });
                  });
              }}
              onDeviceSwitch={async (deviceType) => {
                switch (deviceType) {
                  case 'screen':
                    await mediaStore?.switchEnableScreenSharing();
                    break;
                  default:
                    await mediaStore?.switchEnableDevice(deviceType as MediaSDKDeviceType);
                }
              }}
              devicessPermission={mediaStore?.devicessPermission}
              onAddMembers={() => setOpenAddCallMembers(true)}
              isSingleSession={mediaStore?.isSingleSession}
              {...devicesProps}
              {...maxActionsProps}
            />
          </CallMaxWindowPanel>
        ) : (
          <CallMinWindowPanel
            hoverLeaveRef={hoverLeaveRef}
            bg={bgColor}
            avatar={miniProps?.avatar}
            isConnect={isConnect}
            pindSidePanelChildren={
              isConnect &&
              !isOnly && (
                <PinSidePanel
                  avatar={user?.avatar}
                  avatarSize={200}
                  disabled={true}
                  actions={false}
                  bg={bgColor}
                  member={user}
                  ondbClick={(member) => setUserRoomInfo({ open: true, member })}
                  handleDrawSpectrum={mediaStore.drawSpectrum}
                />
              )
            }
            footerChildren={
              <CallMinActions
                {...actionsProps}
                multiplayer={isCaller ? false : !isSingle}
                mediaType={mediaType}
                isConnect={isConnect}
                videoState={mediaStore?.devices?.videoinput?.status === MediaDeviceStatus.ENABLED}
                setZoom={setFullScreen}
                onHangup={mediaStore?.hangup}
                onAccept={(actionType) => {
                  mediaStore?.join({
                    joinCallType:
                      actionType === MediaActionType.AUDIO
                        ? MsgPayloadType.MEDIA_AUDIO_CALL
                        : MsgPayloadType.MEDIA_VIDEO_CALL,
                  });
                }}
                onDecline={mediaStore?.decline}
                onEnableDevice={() => {
                  mediaStore?.switchEnableDevice(MediaSDKDeviceType.VIDEO_INPUT);
                }}
              />
            }
          >
            {isConnect ? (
              <PinSidePanel
                avatar={isOnly ? user?.avatar : pinSidePanelMember?.avatar}
                disabled={true}
                actions={false}
                member={isOnly ? user : pinSidePanelMember}
                startTime={startTime}
                ondbClick={(member) => setUserRoomInfo({ open: true, member })}
                handleDrawSpectrum={mediaStore.drawSpectrum}
                subscribeCollection={mediaStore.subscribeCollection}
                onSubscribe={handleSubscribe}
              />
            ) : (
              <>
                <CallerInfo
                  avatar={miniProps?.avatar}
                  name={miniProps?.name}
                  isConnect={false}
                  size="lg"
                  showBg
                  hasVolume
                  playbackRate={1}
                />
                <CallStatusDesc
                  isSingle={isSingle}
                  caller={mediaStore?.caller}
                  callMembers={inviteUsers}
                  isCaller={isCaller}
                  callStatus={mediaStore?.callStatus}
                  mediaType={mediaType}
                  fullScreen={fullScreen}
                />
              </>
            )}
          </CallMinWindowPanel>
        )}

        <MediaCallDragLabel
          hoverOverRef={hoverOverRef}
          direction={direction}
          isMouseOver={isMouseOver}
        />
      </MediaCallDrag>
      {userRoomInfo.open && (
        <UserRoomInfo
          open={userRoomInfo.open}
          member={userRoomInfo.member}
          reports={mediaStore?.reports}
          roomNetwork={mediaStore?.roomNetwork}
          onClose={() => setUserRoomInfo({ open: false, member: null })}
        ></UserRoomInfo>
      )}

      <CallMembers
        open={openAddCallMembers}
        members={members}
        callMembers={callMembers}
        onClose={setOpenAddCallMembers}
        onInvite={() => {
          setOpenInviteCallMembers(true);
        }}
      ></CallMembers>

      <CallInviteDialog
        checkUserKey={false}
        isOpen={openInviteCallMembers}
        setIsOpen={setOpenInviteCallMembers}
        sessionId={mediaStore?.session?.chat_id}
        callMembersId={mediaStore?.membersId}
        invitingMembers={mediaStore?.invitingMembers}
        onOK={async (selected) => {
          await mediaStore
            ?.invite({ membersId: selected.map((item) => item.user_id) })
            .catch((error) => {
              mediaError(error, 'invite');
              throw error;
            });
        }}
      ></CallInviteDialog>
    </>
  );
}

export function MediaCurrentDialog({
  mediaActivatedId,
  mediaWaitCalleeId,
}: {
  mediaActivatedId?: string;
  mediaWaitCalleeId?: string;
}) {
  const { t } = useTranslation();
  const result = useMediaCallDialog({
    session_id: mediaActivatedId,
  });

  const { setFullScreen, mediaStore } = result;

  const status = useMemo(() => {
    return mediaStore?.status == MediaStatus.CALLEE
      ? MediaActionStatus.CALLEE
      : mediaStore?.status == MediaStatus.CALL
        ? MediaActionStatus.CALL
        : null;
  }, [mediaStore?.status]);

  useEffect(() => {
    if (mediaStore?.status === MediaStatus.JOIN) {
      setFullScreen(true);
    } else if (!mediaStore?.status || mediaStore?.status === MediaStatus.CREATED) {
      setFullScreen(false);
    }
  }, [mediaStore?.status, setFullScreen]);

  useEffect(() => {
    if (mediaStore?.callStatus === CallStatus.OTHER_DEVICE_ANSWER) {
      toaster.info({ title: t('calls.anotherEndAnswered') });
    }
  }, [mediaStore?.callStatus, t]);

  if (
    (mediaStore?.status === MediaStatus.CREATED ||
      mediaStore?.status === undefined ||
      (mediaWaitCalleeId && mediaStore?.status === MediaStatus.CALLEE)) &&
    mediaStore?.callStatus !== CallStatus.RINGING
  ) {
    return null;
  }

  return <MediaBaseDialog {...result} actionsProps={status ? { status } : {}}></MediaBaseDialog>;
}

export function MediaWaitCalleeDialog({ mediaActivatedId }: { mediaActivatedId: string }) {
  const result = useMediaCallDialog({
    session_id: mediaActivatedId,
  });

  const { mediaStore, session_id, activeMediaStore, mediaType, miniProps, inviteUsers } = result;

  return (
    <CallNewJoin
      isOpen={true}
      mediaType={mediaType}
      avatar={miniProps?.avatar}
      name={miniProps?.name}
      callMembers={inviteUsers}
      onAccept={async (actionType) => {
        await activeMediaStore?.hangup();
        useAppStore.setState({
          mediaActivatedId: session_id,
        });
        await mediaStore?.join({
          joinCallType:
            actionType === MediaActionType.AUDIO
              ? MsgPayloadType.MEDIA_AUDIO_CALL
              : MsgPayloadType.MEDIA_VIDEO_CALL,
        });
        useAppStore.setState({
          mediaWaitCalleeId: undefined,
        });
      }}
      onDecline={async () => {
        await mediaStore?.decline();
        useAppStore.setState({
          mediaWaitCalleeId: undefined,
        });
      }}
    ></CallNewJoin>
  );
}

export default function MediaDialogs() {
  const { mediaActivatedId, mediaWaitCalleeId } = useAppStore();
  return (
    <>
      {mediaWaitCalleeId && <MediaWaitCalleeDialog mediaActivatedId={mediaWaitCalleeId} />}

      <MediaCurrentDialog
        mediaActivatedId={mediaActivatedId}
        mediaWaitCalleeId={mediaWaitCalleeId}
      />
    </>
  );
}
